import React, { useState, useEffect, useRef, useCallback } from "react";
import { Video } from "../../types/Video";
import {
  VideoSyncService,
  CommentData,
  SkipVoteStatus,
} from "../../services/videoService";
import CommentDanmaku from "../../components/CommentDanmaku/CommentDanmaku";
import SkipVoteButton from "../../components/SkipVoteButton/SkipVoteButton";
import SkipVotePanel from "../../components/SkipVotePanel/SkipVotePanel";
import SkipVoteModal from "../../components/SkipVoteModal/SkipVoteModal";
import VideoInfoSection from "../../components/VideoInfoSection/VideoInfoSection";
import CompactChat from "../../components/CompactChat/CompactChat";
import ExpandedChatOverlay from "../../components/ExpandedChatOverlay/ExpandedChatOverlay";
import AdminLoginModal from "../../components/AdminLoginModal/AdminLoginModal";
import AdminToolbar from "../../components/AdminToolbar/AdminToolbar";
import { VOTING_ENABLED } from "../../constants/feature-flags";
import {
  VolumeIcon,
  CommentIcon,
  CommentOffIcon,
  SendIcon,
  FullScreenIcon,
  ExitFullScreenIcon,
  ChatToggleIcon,
} from "./icons";
import initialVideos from "../../data/initial_videos.json";
import "./WatchTogetherPage.css";

// ローカルユーティリティ・型
import { PlayerState, ConnectionStatus } from "./types";
import {
  YT_PLAYER_STATES,
  parseDurationToSeconds,
  formatCommentTimestamp,
  createVideoObject,
  generateUserId,
} from "./utils/watchTogetherUtils";

const WatchTogetherPage: React.FC = () => {
  /// 基本状態
  const [currentVideo, setCurrentVideo] = useState<Video | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const [showInitialPlayButton, setShowInitialPlayButton] = useState(true); // 初回再生ボタン表示フラグ

  /// 接続状態
  const isMountedRef = useRef(true);
  const retryInitializeTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (retryInitializeTimeoutRef.current != null) {
        clearTimeout(retryInitializeTimeoutRef.current);
        retryInitializeTimeoutRef.current = null;
      }
    };
  }, []);

  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>("connecting");

  /// プレイヤー状態
  const [playerState, setPlayerState] = useState<PlayerState>({
    videoId: "",
    currentTime: 0,
    duration: 0,
    state: "UNSTARTED",
  });

  /// 音声制御
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(true);

  /// コメント機能
  const [comments, setComments] = useState<CommentData[]>([]);
  const [commentText, setCommentText] = useState("");

  /// ユーザーIDの初期化（ブラウザ固有、永続化）
  const [userId] = useState(() => {
    let storedUserId = localStorage.getItem("watch-together-user-id");
    if (!storedUserId) {
      storedUserId = generateUserId();
      localStorage.setItem("watch-together-user-id", storedUserId);
    }
    return storedUserId;
  });

  /// ユーザー名の初期化
  const [username, setUsername] = useState(() => {
    return localStorage.getItem("watch-together-username") || "匿名";
  });
  const [showDanmaku, setShowDanmaku] = useState(true);
  const [isCommentHistoryLoaded, setIsCommentHistoryLoaded] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  /// 視聴者数
  const [viewerCount, setViewerCount] = useState(0);

  /// スキップ投票
  const [skipVoteActive, setSkipVoteActive] = useState(false);
  const [skipVoteStatus, setSkipVoteStatus] = useState<SkipVoteStatus | null>(
    null
  );
  const [skipVoteCountdown, setSkipVoteCountdown] = useState(0);
  const [userVoted, setUserVoted] = useState<"SKIP" | "STAY" | null>(null);
  const [isSkipVoteModalOpen, setIsSkipVoteModalOpen] = useState(false);

  /// 管理者UI
  const [isAdminLoginOpen, setIsAdminLoginOpen] = useState(false);
  // クエリパラメータ ?admin=1 の有無でエントリ（ログイン導線）のみを制御し、
  // 管理者モード（ツール表示）はログイン成功後にのみ有効化する
  const [shouldShowAdminEntry] = useState(
    () => new URLSearchParams(window.location.search).get("admin") === "1"
  );
  const [isAdminMode, setIsAdminMode] = useState(false);

  /// プレイヤーコンテナのタップ時UI表示制御
  const [showPlayerControls, setShowPlayerControls] = useState(false);
  const playerControlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /// 全画面モード制御
  const [isFullScreenMode, setIsFullScreenMode] = useState(false);

  /// 全画面時のチャット表示制御
  const [isChatVisible, setIsChatVisible] = useState(true);

  /// スマホレイアウト関連の状態
  const [isChatExpanded, setIsChatExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  /// Refs
  const playerRef = useRef<any>(null);
  const syncServiceRef = useRef<VideoSyncService | null>(null);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  const timeUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const initializingRef = useRef(false); // 初期化中フラグ（React Strict Mode対応）

  /// Stale closure問題を解決するためにuseRefで最新のstateを保持
  const currentVideoRef = useRef(currentVideo);
  currentVideoRef.current = currentVideo;

  const isPlayerReadyRef = useRef(isPlayerReady);
  isPlayerReadyRef.current = isPlayerReady;

  const playerStateRef = useRef(playerState);
  playerStateRef.current = playerState;

  const userInteractedRef = useRef(userInteracted);
  userInteractedRef.current = userInteracted;

  const showInitialPlayButtonRef = useRef(showInitialPlayButton);
  showInitialPlayButtonRef.current = showInitialPlayButton;

  /// LocalStorageからユーザー操作フラグを復元
  useEffect(() => {
    const savedUserInteracted = localStorage.getItem(
      "watch-together-user-interacted"
    );
    if (savedUserInteracted === "true") {
      setUserInteracted(true);
      // リロード時にはブラウザの自動再生制限により、再度ユーザー操作が必要
      // そのため showInitialPlayButton は true のままにしておく
    }
  }, []);

  /// チャットの自動スクロール処理
  useEffect(() => {
    if (shouldAutoScroll && chatMessagesRef.current) {
      const container = chatMessagesRef.current;
      const scrollToBottom = () => {
        container.scrollTop = container.scrollHeight;
      };

      // 少し遅延してからスクロール（DOM更新待ち）
      const timeoutId = setTimeout(scrollToBottom, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [comments, shouldAutoScroll]);

  /// チャットスクロール位置の監視
  const handleChatScroll = useCallback(() => {
    if (!chatMessagesRef.current) return;

    const container = chatMessagesRef.current;
    // スマホでは判定の余裕を少なくして、より敏感にスクロール検出
    const scrollThreshold = window.innerWidth <= 480 ? 20 : 50;
    const isAtBottom =
      container.scrollHeight - container.scrollTop <=
      container.clientHeight + scrollThreshold;

    setShouldAutoScroll(isAtBottom);
    setShowScrollToBottom(!isAtBottom);
  }, []);

  /// タッチデバイスでのスクロール検出を改善
  const handleTouchStart = useCallback(() => {
    // タッチスクロール開始時に自動スクロールを一時停止
    setShouldAutoScroll(false);
  }, []);

  const handleTouchEnd = useCallback(() => {
    // タッチ終了時にスクロール位置をチェック
    setTimeout(() => {
      handleChatScroll();
    }, 150); // タッチ終了後に少し遅延を入れて正確な位置を取得
  }, [handleChatScroll]);

  /// 最下部へスクロールする関数
  const scrollToBottom = useCallback(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
      setShouldAutoScroll(true);
      setShowScrollToBottom(false);
    }
  }, []);

  /// 同期サービス初期化
  const initializeSyncService = useCallback(async () => {
    if (syncServiceRef.current) {
      return;
    }

    try {
      const syncService = new VideoSyncService();

      // イベントハンドラーを設定
      syncService.onConnection((connected) => {
        if (connected) {
          setConnectionStatus("connected");

          // 初回接続時にコメント履歴を取得
          if (!isCommentHistoryLoaded) {
            syncService.requestCommentHistory();
          }
        } else {
          // 再接続試行中か完全な切断かを判定
          const isReconnecting =
            syncService.isConnected === false && syncService.getClientId;
          setConnectionStatus(isReconnecting ? "reconnecting" : "disconnected");

          if (!isReconnecting) {
            setIsLoading(true);
          }
        }
      });

      syncService.onViewerCount((count) => {
        setViewerCount(count);
      });

      // サーバーからの同期データを受信
      syncService.onSync((syncState) => {
        const currentVideo = currentVideoRef.current;
        const isPlayerReady = isPlayerReadyRef.current;
        const userInteracted = userInteractedRef.current;

        const durationInSeconds =
          typeof syncState.duration === "number"
            ? syncState.duration
            : parseInt(String(syncState.duration), 10) ||
              (currentVideo
                ? parseDurationToSeconds(currentVideo.duration)
                : 0);

        // 新しい動画IDの場合は、動画情報更新を最優先で処理
        if (!currentVideo || currentVideo.videoId !== syncState.videoId) {
          const detailVideo = initialVideos.find(
            (v) => v.videoId === syncState.videoId
          );

          if (detailVideo) {
            const serverVideo: Video = {
              id: syncState.videoId,
              videoId: syncState.videoId,
              title: detailVideo.title,
              description: detailVideo.description || "",
              publishedAt: detailVideo.publishedAt || new Date().toISOString(),
              duration: detailVideo.duration,
              viewCount: detailVideo.viewCount || 0,
              likeCount: detailVideo.likeCount || 0,
              tags: detailVideo.tags || [],
              videoUrl: `https://www.youtube.com/watch?v=${syncState.videoId}`,
              thumbnailId: syncState.videoId,
              thumbnailPath: `/assets/images/thumbnails/${syncState.videoId}.jpg`,
            };

            setCurrentVideo(serverVideo);
            setIsLoading(true); // 新しい動画を読み込み中に設定
          }
        }

        setPlayerState((prev) => ({
          videoId: syncState.videoId,
          currentTime: syncState.currentTime,
          state: syncState.state,
          duration: durationInSeconds,
        }));

        // プレイヤーが準備できていない場合は同期をスキップ
        if (!isPlayerReady || !playerRef.current) {
          return;
        }

        // 現在の動画と同じかどうかをチェック
        if (currentVideo && currentVideo.videoId === syncState.videoId) {
          try {
            const currentPlayerTime = playerRef.current.getCurrentTime() || 0;
            const currentPlayerState = playerRef.current.getPlayerState();

            // 時間の同期
            const timeDiff = Math.abs(
              currentPlayerTime - syncState.currentTime
            );
            const syncThreshold = showInitialPlayButtonRef.current ? 0.2 : 0.8;

            if (timeDiff > syncThreshold) {
              playerRef.current.seekTo(syncState.currentTime, true);
              setPlayerState((prevState) => ({
                ...prevState,
                currentTime: syncState.currentTime,
              }));
            }

            // 状態の同期（ユーザー操作後のみ）
            if (userInteracted) {
              if (
                syncState.state === "PLAYING" &&
                currentPlayerState !== 1 &&
                currentPlayerState !== 3
              ) {
                playerRef.current.playVideo();
              } else if (
                syncState.state === "PAUSED" &&
                currentPlayerState === 1
              ) {
                playerRef.current.pauseVideo();
              }
            }
          } catch (error) {
            console.error("Sync error:", error);
          }
        }
      });

      // サーバーからの動画情報を受信
      syncService.onServerVideo((videoInfo) => {
        const currentVideo = currentVideoRef.current;
        if (currentVideo && currentVideo.videoId === videoInfo.videoId) {
          return; // 同じ動画の場合はスキップ
        }

        const detailVideo = initialVideos.find(
          (v) => v.videoId === videoInfo.videoId
        );
        if (detailVideo) {
          const newVideo = createVideoObject(detailVideo, videoInfo);
          setCurrentVideo(newVideo);
          setIsLoading(true);
        }
      });

      // コメント受信
      syncService.onComment((comment) => {
        setComments((prev) => [...prev, comment]);
      });

      // コメント履歴受信
      syncService.onCommentHistory((historyComments) => {
        setComments(historyComments);
        setIsCommentHistoryLoaded(true);
      });

      if (VOTING_ENABLED) {
        // スキップ投票状態受信
        syncService.onSkipVoteStatus((status) => {
          setSkipVoteStatus(status);
          setSkipVoteActive(true);

          // クライアントの投票状況を復元
          if (status.clientVote) {
            setUserVoted(status.clientVote);
          }

          // モバイル端末で未投票の場合、モーダルを自動表示
          if (!status.clientVote && window.innerWidth <= 1024) {
            setIsSkipVoteModalOpen(true);
          }
        });

        // スキップ投票結果受信
        syncService.onSkipVoteResult(() => {
          setSkipVoteActive(false);
          setSkipVoteStatus(null);
          setUserVoted(null);
          setIsSkipVoteModalOpen(false);
        });
      }

      syncServiceRef.current = syncService;

      // 接続開始
      setConnectionStatus("connecting");
      const connected = await syncService.connect();
      if (connected) {
        setConnectionStatus("connected");
        syncService.requestSync();
      } else {
        console.error("Failed to connect VideoSyncService");
        setConnectionStatus("disconnected");
      }
    } catch (error) {
      console.error("Failed to initialize VideoSyncService:", error);
    }
  }, [isCommentHistoryLoaded]); // showInitialPlayButtonをrefに変更したため依存配列から削除

  /// API・サービス初期化とクリーンアップ
  useEffect(() => {
    const initializeYouTubeAPI = () => {
      return new Promise<void>((resolve, reject) => {
        if (window.YT && window.YT.Player) {
          resolve();
          return;
        }
        const script = document.createElement("script");
        script.src = "https://www.youtube.com/iframe_api";
        script.async = true;
        script.onload = () => resolve();
        script.onerror = (error) => reject(error);
        document.head.appendChild(script);
        window.onYouTubeIframeAPIReady = () => resolve();
      });
    };

    const initialize = async () => {
      try {
        setConnectionStatus("connecting");
        await initializeYouTubeAPI();
        await initializeSyncService();
      } catch (error) {
        console.error("❌ Initialization failed:", error);
        setConnectionStatus("disconnected");
        setIsLoading(false);
      }
    };

    initialize();

    return () => {
      if (syncServiceRef.current) {
        syncServiceRef.current.disconnect();
      }
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          /* ignore */
        }
        playerRef.current = null;
      }
    };
  }, [initializeSyncService]);

  /// DOM要素の存在確認を行う関数（シンプル実装）
  const waitForPlayerElement = (): Promise<HTMLElement> => {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 20; // 1秒以内（50ms × 20回）

      let timeoutId: number | null = null;

      const check = () => {
        if (!isMountedRef.current) {
          if (timeoutId != null) {
            clearTimeout(timeoutId);
          }
          reject(
            new Error("Player initialization cancelled (component unmounted)")
          );
          return;
        }
        const element = document.getElementById("youtube-player");
        if (element) {
          if (timeoutId != null) {
            clearTimeout(timeoutId);
          }
          resolve(element);
          return;
        }

        attempts++;
        if (attempts >= maxAttempts) {
          if (timeoutId != null) {
            clearTimeout(timeoutId);
          }
          reject(
            new Error(
              `Player element not found after ${maxAttempts} attempts (${
                maxAttempts * 50
              }ms)`
            )
          );
          return;
        }

        timeoutId = window.setTimeout(check, 50);
      };

      check();
    });
  };

  /// プレイヤー初期化（シンプル実装）
  const initializePlayer = useCallback(async () => {
    // 重複実行防止
    if (initializingRef.current) {
      return;
    }

    const currentVideo = currentVideoRef.current;
    if (!currentVideo || !window.YT || !window.YT.Player) {
      console.warn("Cannot initialize player: missing requirements", {
        hasCurrentVideo: !!currentVideo,
        hasYouTubeAPI: !!(window.YT && window.YT.Player),
      });
      return;
    }

    if (!isMountedRef.current) {
      return;
    }

    initializingRef.current = true;
    setIsInitializing(true);

    try {
      // 既存のプレイヤーを確実にクリーンアップ
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (error) {
          console.warn("⚠️ Error destroying player:", error);
        }
        playerRef.current = null;
      }

      // プレイヤーの準備状態もリセット
      setIsPlayerReady(false);

      // DOM要素の存在を確認（1秒以内）
      const playerElement = await waitForPlayerElement();
      if (!isMountedRef.current) {
        return;
      }

      // 既存のプレイヤーをクリア
      playerElement.innerHTML = "";

      // 少し待ってからプレイヤーを作成
      await new Promise((resolve) => setTimeout(resolve, 100));
      if (!isMountedRef.current) {
        return;
      }

      playerRef.current = new window.YT.Player("youtube-player", {
        height: "100%",
        width: "100%",
        videoId: currentVideo.videoId,
        playerVars: {
          autoplay: 0,
          controls: 0,
          disablekb: 1,
          fs: 0,
          iv_load_policy: 3,
          modestbranding: 1,
          rel: 0,
          showinfo: 0,
          enablejsapi: 1,
          cc_load_policy: 0,
          playsinline: 1,
          mute: 1,
          origin: window.location.origin,
        },
        events: {
          onReady: onPlayerReady,
          onStateChange: onPlayerStateChange,
          onError: onPlayerError,
        },
      });
    } catch (error) {
      console.error("❌ Failed to create YouTube Player:", error);
      if (isMountedRef.current) {
        setIsLoading(false);
      }

      // 3秒後に自動再試行
      if (!isMountedRef.current) {
        return;
      }
      if (retryInitializeTimeoutRef.current != null) {
        clearTimeout(retryInitializeTimeoutRef.current);
      }
      retryInitializeTimeoutRef.current = window.setTimeout(() => {
        if (!isMountedRef.current) {
          return;
        }
        if (!playerRef.current && currentVideoRef.current) {
          initializePlayer().catch((retryError) => {
            console.error("❌ Retry failed:", retryError);
          });
        }
      }, 3000);
    } finally {
      initializingRef.current = false;
      if (isMountedRef.current) {
        setIsInitializing(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // イベントハンドラーは後で定義されるため、依存関係配列は空にする

  /// プレイヤー準備完了時のコールバック
  const onPlayerReady = (event: any) => {
    playerRef.current = event.target;
    setIsPlayerReady(true);
    setIsLoading(false);

    try {
      // 音声設定
      const savedVolume = parseInt(
        localStorage.getItem("watch-together-volume") || "80"
      );
      if (typeof playerRef.current.setVolume === "function") {
        playerRef.current.setVolume(savedVolume);
      }
      if (typeof playerRef.current.mute === "function") {
        playerRef.current.mute();
      }
      setVolume(savedVolume);
      setIsMuted(true);

      // 画質を最高品質に設定
      if (typeof playerRef.current.setPlaybackQuality === "function") {
        // 利用可能な画質レベルを取得
        const availableQualities =
          playerRef.current.getAvailableQualityLevels?.() || [];

        // 最高画質を選択（hd1080 > hd720 > large > medium > small の優先順）
        const preferredQualities = [
          "hd1080",
          "hd720",
          "large",
          "medium",
          "small",
        ];
        for (const quality of preferredQualities) {
          if (availableQualities.includes(quality)) {
            playerRef.current.setPlaybackQuality(quality);
            break;
          }
        }
      }
    } catch (error) {
      console.error("プレイヤー設定エラー:", error);
    }
  };

  /// プレイヤー状態変更
  const onPlayerStateChange = (event: any) => {
    const state = event.data;
    const userInteracted = userInteractedRef.current;

    let newState: PlayerState["state"];
    switch (state) {
      case YT_PLAYER_STATES.UNSTARTED:
        newState = "UNSTARTED";
        break;
      case YT_PLAYER_STATES.ENDED:
        newState = "ENDED";
        break;
      case YT_PLAYER_STATES.PLAYING:
        newState = "PLAYING";
        setIsLoading(false);
        break;
      case YT_PLAYER_STATES.PAUSED:
        newState = "PAUSED";
        break;
      case YT_PLAYER_STATES.BUFFERING:
        newState = "BUFFERING";
        break;
      case YT_PLAYER_STATES.CUED:
        newState = "CUED";
        if (userInteracted && !showInitialPlayButton) {
          setTimeout(() => {
            if (
              playerRef.current &&
              typeof playerRef.current.playVideo === "function"
            ) {
              try {
                playerRef.current.playVideo();
              } catch (error) {
                console.warn("Autoplay failed for cued video:", error);
              }
            }
          }, 500);
        }
        break;
      default:
        newState = "UNSTARTED";
    }

    let currentTime = 0;
    try {
      if (
        playerRef.current &&
        typeof playerRef.current.getCurrentTime === "function"
      ) {
        currentTime = playerRef.current.getCurrentTime() || 0;
      }
    } catch (error) {
      console.warn("getCurrentTime error:", error);
    }

    setPlayerState((prev) => {
      if (
        Math.abs(prev.currentTime - currentTime) > 0.1 ||
        prev.state !== newState
      ) {
        return { ...prev, currentTime, state: newState };
      }
      return prev;
    });
  };

  /// プレイヤーエラー
  const onPlayerError = (event: any) => {
    console.error("YouTube Player error:", event.data);
    setIsLoading(false);
  };

  /// currentVideoが変更されたときにプレイヤーを初期化
  useEffect(() => {
    const currentVideo = currentVideoRef.current;

    if (
      currentVideo &&
      window.YT &&
      window.YT.Player &&
      !initializingRef.current
    ) {
      initializePlayer().catch((error) => {
        console.error("Failed to initialize player:", error);
      });
    }
  }, [currentVideo?.videoId, initializePlayer]); // initializePlayerを依存関係に追加

  /// プレイヤーと動画の準備が整ったら動画を読み込む
  useEffect(() => {
    const currentVideo = currentVideoRef.current;
    const isPlayerReady = isPlayerReadyRef.current;

    if (
      !currentVideo ||
      !isPlayerReady ||
      !playerRef.current ||
      typeof playerRef.current.loadVideoById !== "function"
    ) {
      return;
    }

    try {
      let currentPlayingVideoId = null;
      if (
        playerRef.current &&
        typeof playerRef.current.getVideoUrl === "function"
      ) {
        try {
          const currentVideoUrl = playerRef.current.getVideoUrl();
          currentPlayingVideoId = currentVideoUrl
            ? new URL(currentVideoUrl).searchParams.get("v")
            : null;
        } catch (urlError) {
          console.warn("⚠️ Error parsing video URL:", urlError);
        }
      }

      if (currentPlayingVideoId === currentVideo.videoId) {
        return;
      }

      setPlayerState((prevState) => ({
        ...prevState,
        videoId: currentVideo.videoId,
        currentTime: 0,
        state: "UNSTARTED",
      }));

      playerRef.current.loadVideoById({
        videoId: currentVideo.videoId,
        startSeconds: 0,
        suggestedQuality: "hd1080", // 1080p画質を優先
      });

      // 動画読み込み後に画質を再設定（動画によって利用可能な画質が異なるため）
      setTimeout(() => {
        if (
          playerRef.current &&
          typeof playerRef.current.setPlaybackQuality === "function"
        ) {
          try {
            const availableQualities =
              playerRef.current.getAvailableQualityLevels?.() || [];

            const preferredQualities = [
              "hd1080",
              "hd720",
              "large",
              "medium",
              "small",
            ];
            for (const quality of preferredQualities) {
              if (availableQualities.includes(quality)) {
                playerRef.current.setPlaybackQuality(quality);
                break;
              }
            }
          } catch (error) {
            console.warn("動画読み込み後の画質設定エラー:", error);
          }
        }
      }, 2000); // 2秒後に実行（動画の準備完了を待つ）
    } catch (error) {
      console.error("Failed to load video:", error);
      setIsLoading(false);
    }
  }, [currentVideo, isPlayerReady]);

  /// スキップ投票カウントダウン
  useEffect(() => {
    if (!VOTING_ENABLED) return;
    if (!skipVoteActive || !skipVoteStatus) return;

    const updateCountdown = () => {
      const remaining = Math.ceil((skipVoteStatus.endsAt - Date.now()) / 1000);
      setSkipVoteCountdown(Math.max(0, remaining));

      if (remaining <= 0) {
        setSkipVoteActive(false);
        setSkipVoteStatus(null);
        setUserVoted(null);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [skipVoteActive, skipVoteStatus]);

  /// スキップ投票モーダルの自動表示（モバイル端末）
  useEffect(() => {
    if (!VOTING_ENABLED) return;
    // 投票がアクティブ & 未投票 かつ モバイル画面幅の場合にモーダルを表示
    if (skipVoteActive && !userVoted) {
      if (window.innerWidth <= 1024) {
        setIsSkipVoteModalOpen(true);
      }
    }
  }, [skipVoteActive, userVoted]);

  /// ユーザー名変更処理
  const handleUsernameChange = useCallback((newUsername: string) => {
    setUsername(newUsername);
    localStorage.setItem("watch-together-username", newUsername);
  }, []);

  /// 音量制御
  const handleVolumeChange = useCallback(
    (newVolume: number) => {
      setVolume(newVolume);
      localStorage.setItem("watch-together-volume", newVolume.toString());

      if (
        playerRef.current &&
        typeof playerRef.current.setVolume === "function"
      ) {
        try {
          playerRef.current.setVolume(newVolume);
          if (newVolume > 0 && isMuted) {
            playerRef.current.unMute();
            setIsMuted(false);
          }
        } catch (error) {
          console.error("Volume change error:", error);
        }
      }
    },
    [isMuted]
  );

  const handleMuteToggle = useCallback(() => {
    if (playerRef.current) {
      try {
        if (isMuted) {
          playerRef.current.unMute();
          setIsMuted(false);
        } else {
          playerRef.current.mute();
          setIsMuted(true);
        }
      } catch (error) {
        console.error("Mute toggle error:", error);
      }
    }
  }, [isMuted]);

  /// コメント送信
  const handleCommentSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!commentText.trim() || !username.trim()) {
        return;
      }

      if (syncServiceRef.current) {
        // userIdを含めてコメントを送信
        if (
          typeof (syncServiceRef.current as any).sendCommentWithUserId ===
          "function"
        ) {
          (syncServiceRef.current as any).sendCommentWithUserId(
            userId,
            username.trim(),
            commentText.trim()
          );
        } else {
          // フォールバック：古いメソッドを使用
          syncServiceRef.current.sendComment(
            username.trim(),
            commentText.trim()
          );
        }
        setCommentText("");
      }
    },
    [commentText, username, userId]
  );

  /// コメント弾幕の表示/非表示を切り替える
  const handleDanmakuToggle = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setShowDanmaku(!showDanmaku);
    },
    [showDanmaku]
  );

  /// スキップ投票
  const handleSkipVoteStart = useCallback(() => {
    if (syncServiceRef.current) {
      if (skipVoteActive) {
        setIsSkipVoteModalOpen(true);
      } else {
        if (VOTING_ENABLED) {
          syncServiceRef.current.startSkipVote();
        }
      }
    }
  }, [skipVoteActive]);

  const handleSkipVote = useCallback(
    (choice: "SKIP" | "STAY") => {
      if (syncServiceRef.current && skipVoteActive && !userVoted) {
        syncServiceRef.current.sendSkipVote(choice);
        setUserVoted(choice);
      }
    },
    [skipVoteActive, userVoted]
  );

  /// 時間更新用のタイマー（1秒ごとに実行）
  useEffect(() => {
    if (
      playerStateRef.current.state === "PLAYING" &&
      playerStateRef.current.duration > 0
    ) {
      timeUpdateIntervalRef.current = setInterval(() => {
        setPlayerState((prevState) => {
          if (prevState.state === "PLAYING" && prevState.duration > 0) {
            const newCurrentTime = Math.min(
              prevState.currentTime + 1,
              prevState.duration
            );

            // 10秒ごとにサーバーとの同期を確認（タイマー内で判定）
            // currentTimeが浮動小数点数の場合を考慮して、整数部分で判定
            if (
              Math.floor(newCurrentTime) % 10 === 0 &&
              Math.floor(prevState.currentTime) % 10 !== 0
            ) {
              if (syncServiceRef.current) {
                syncServiceRef.current.requestSync();
              }
            }

            return {
              ...prevState,
              currentTime: newCurrentTime,
            };
          }
          return prevState;
        });
      }, 1000);
    } else {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = null;
      }
    }

    return () => {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
      }
    };
  }, [playerState.state, playerState.duration]); // playerStateを監視

  /// クリーンアップ
  useEffect(() => {
    return () => {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
      }
      if (playerControlsTimeoutRef.current) {
        clearTimeout(playerControlsTimeoutRef.current);
      }
    };
  }, []);

  /// 初回再生ボタンのクリック処理（自動再生制限解除）
  const handleInitialPlay = useCallback(() => {
    setUserInteracted(true);
    setShowInitialPlayButton(false);

    // LocalStorageにユーザー操作フラグを保存
    localStorage.setItem("watch-together-user-interacted", "true");

    if (playerRef.current) {
      try {
        // 音声設定を再適用
        const savedVolume = parseInt(
          localStorage.getItem("watch-together-volume") || "80"
        );

        if (typeof playerRef.current.setVolume === "function") {
          playerRef.current.setVolume(savedVolume);
        }

        // 初期ミュート解除（ユーザーが明示的に再生を開始したため）
        if (typeof playerRef.current.unMute === "function") {
          playerRef.current.unMute();
          setIsMuted(false);
        }

        // サーバー同期を要求して、その後再生を開始
        if (syncServiceRef.current) {
          syncServiceRef.current.requestSync();

          // 少し遅延してから現在のサーバー状態に基づいて再生開始
          setTimeout(() => {
            if (playerRef.current) {
              try {
                // サーバー状態が PLAYING の場合は時間合わせをして再生
                if (playerStateRef.current.state === "PLAYING") {
                  if (typeof playerRef.current.seekTo === "function") {
                    playerRef.current.seekTo(
                      playerStateRef.current.currentTime,
                      true
                    );
                  }
                }

                // 再生開始
                if (typeof playerRef.current.playVideo === "function") {
                  playerRef.current.playVideo();
                }
              } catch (error) {
                console.error("Initial play error:", error);
              }
            }
          }, 1000);
        }
      } catch (error) {
        console.error("Initial play setup error:", error);
      }
    } else {
      console.error("Player reference not available during initial play");
    }
  }, []); // refの値は依存関係に含める必要がない

  /// プレイヤーコンテナのタップ時UI表示制御
  const handlePlayerControls = useCallback(() => {
    // モバイル端末でのみ有効
    if (window.innerWidth <= 1024) {
      if (playerControlsTimeoutRef.current) {
        clearTimeout(playerControlsTimeoutRef.current);
      }

      setShowPlayerControls(true);

      // 3秒後に自動非表示
      playerControlsTimeoutRef.current = setTimeout(() => {
        setShowPlayerControls(false);
      }, 3000);
    }
  }, []);

  /// 全画面モード切り替え（横画面固定付き）
  const handleFullScreenToggle = useCallback(async () => {
    if (!isFullScreenMode) {
      // 全画面モードに入る + 横画面に固定
      try {
        // 1. フルスクリーンモードに入る
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }

        // 2. 横画面に固定（フルスクリーン時のみ動作）
        if ("screen" in window && "orientation" in window.screen) {
          const orientation = window.screen.orientation as any;
          if (orientation && typeof orientation.lock === "function") {
            try {
              await orientation.lock("landscape");
            } catch (orientationError) {
              console.warn(
                "横画面固定に失敗（ブラウザが対応していない可能性）:",
                orientationError
              );
            }
          }
        }

        // 3. モバイルで全画面に入る時はチャット表示状態を初期化
        if (window.innerWidth <= 768) {
          setIsChatVisible(true); // デフォルトでチャット表示
        }

        setIsFullScreenMode(true);
      } catch (error) {
        console.warn("フルスクリーンに失敗:", error);
        // フルスクリーンに失敗してもCSSによる疑似全画面は有効にする
        if (window.innerWidth <= 768) {
          setIsChatVisible(true);
        }
        setIsFullScreenMode(true);
      }
    } else {
      // 全画面モード終了 + 画面固定解除
      try {
        if (document.fullscreenElement && document.exitFullscreen) {
          await document.exitFullscreen();
        }

        // 画面固定解除
        if ("screen" in window && "orientation" in window.screen) {
          const orientation = window.screen.orientation as any;
          if (orientation && typeof orientation.unlock === "function") {
            try {
              orientation.unlock();
            } catch (orientationError) {
              console.warn("画面固定解除に失敗:", orientationError);
            }
          }
        }
      } catch (error) {
        console.warn("フルスクリーン終了に失敗:", error);
      }

      setIsFullScreenMode(false);
    }
  }, [isFullScreenMode]);

  /// 全画面時のチャット表示切り替え
  const handleChatToggle = useCallback(() => {
    setIsChatVisible(!isChatVisible);
  }, [isChatVisible]);

  /// スマホレイアウト用チャット展開ハンドラー
  const handleChatExpand = useCallback(() => {
    setIsChatExpanded(true);
  }, []);

  /// スマホレイアウト用チャット閉じるハンドラー
  const handleChatClose = useCallback(() => {
    setIsChatExpanded(false);
  }, []);

  /// チャット展開状態に応じてbody要素のクラスを管理
  useEffect(() => {
    if (isChatExpanded) {
      document.body.classList.add("chat-overlay-open");
    } else {
      document.body.classList.remove("chat-overlay-open");
    }

    // クリーンアップ: コンポーネントがアンマウントされた時にクラスを削除
    return () => {
      document.body.classList.remove("chat-overlay-open");
    };
  }, [isChatExpanded]);

  /// コメントテキスト変更ハンドラー
  const handleCommentTextChange = useCallback((text: string) => {
    setCommentText(text);
  }, []);

  /// フルスクリーン状態の監視
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!document.fullscreenElement;

      // ブラウザのフルスクリーン状態と内部状態を同期
      if (!isCurrentlyFullscreen && isFullScreenMode) {
        // ESCキーなどでフルスクリーンを抜けた場合
        setIsFullScreenMode(false);

        // 画面固定も解除
        if ("screen" in window && "orientation" in window.screen) {
          const orientation = window.screen.orientation as any;
          if (orientation && typeof orientation.unlock === "function") {
            try {
              orientation.unlock();
            } catch (error) {
              console.warn("画面固定解除に失敗:", error);
            }
          }
        }
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [isFullScreenMode]);

  /// 全画面モード時の動的高さ調整
  useEffect(() => {
    if (!isFullScreenMode) return;

    const adjustFullScreenHeight = () => {
      // 実際のビューポート高さを取得
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--real-vh", `${vh}px`);

      // モバイルでのキーボード表示対応
      if ("visualViewport" in window) {
        const viewport = window.visualViewport!;
        const updateViewportHeight = () => {
          const currentVh = viewport.height * 0.01;
          document.documentElement.style.setProperty(
            "--real-vh",
            `${currentVh}px`
          );
        };

        viewport.addEventListener("resize", updateViewportHeight);
        updateViewportHeight();

        return () => {
          viewport.removeEventListener("resize", updateViewportHeight);
        };
      }
    };

    adjustFullScreenHeight();
    window.addEventListener("resize", adjustFullScreenHeight);
    window.addEventListener("orientationchange", adjustFullScreenHeight);

    return () => {
      window.removeEventListener("resize", adjustFullScreenHeight);
      window.removeEventListener("orientationchange", adjustFullScreenHeight);
      document.documentElement.style.removeProperty("--real-vh");
    };
  }, [isFullScreenMode]);

  /// レスポンシブ監視 - スマホレイアウト切り替え用
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 1024;
      setIsMobile(newIsMobile);

      // デスクトップに戻った時はチャットを閉じる
      if (!newIsMobile && isChatExpanded) {
        setIsChatExpanded(false);
      }

      // 全画面時の画面回転処理
      if (isFullScreenMode && newIsMobile) {
        // 縦画面（portrait）の場合はチャットを非表示
        // 横画面（landscape）の場合はチャットを表示
        const isLandscape = window.innerHeight < window.innerWidth;
        if (!isLandscape) {
          // 縦画面ではチャット非表示
          setIsChatVisible(false);
        } else {
          // 横画面ではチャット表示（6:4レイアウト）
          setIsChatVisible(true);
        }
      }
    };

    // orientationchangeイベントも追加
    const handleOrientationChange = () => {
      // orientationchangeの後は少し遅延してからリサイズ処理を実行
      setTimeout(handleResize, 150);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleOrientationChange);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, [isChatExpanded, isFullScreenMode]);

  if (isLoading) {
    return (
      <div className="watch-together-page">
        <div className="watch-together-page__loading">
          <div className="loading-spinner"></div>
          <p>動画を読み込み中...</p>
        </div>
      </div>
    );
  }

  /// モバイルレイアウトのレンダリング
  const renderMobileLayout = () => (
    <div
      className={`watch-together-page watch-together-page--mobile ${
        isFullScreenMode ? "watch-together-page--fullscreen" : ""
      } ${isFullScreenMode && !isChatVisible ? "chat-hidden" : ""}`}
    >
      {/* プレイヤーセクション（ヘッダーなし） */}
      <section className="watch-together-page__player-section">
        {/* プレイヤー */}
        <div
          className="watch-together-page__player-container"
          ref={playerContainerRef}
          onClick={handlePlayerControls}
        >
          <div
            id="youtube-player"
            className="watch-together-page__player"
          ></div>

          {/* プレイヤー読み込み中の表示 */}
          {(isInitializing ||
            !currentVideoRef.current ||
            !isPlayerReadyRef.current) && (
            <div className="watch-together-page__player-loading">
              <div className="loading-spinner"></div>
              <p>
                {isInitializing
                  ? "プレイヤーを初期化中..."
                  : "プレイヤーを読み込み中..."}
              </p>
            </div>
          )}

          {/* 初回再生ボタンオーバーレイ */}
          {showInitialPlayButton && isPlayerReadyRef.current && (
            <div className="watch-together-page__initial-play-overlay">
              <button
                className="initial-play-button"
                onClick={handleInitialPlay}
                type="button"
              >
                <div className="play-icon">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
                    <circle
                      cx="12"
                      cy="12"
                      r="12"
                      fill="rgba(255, 255, 255, 0.9)"
                    />
                    <polygon points="10,8 16,12 10,16" fill="#333" />
                  </svg>
                </div>
                <span className="play-text">再生開始</span>
                <span className="play-subtext">クリックして視聴を開始</span>
              </button>
            </div>
          )}

          {/* スマホ用音量コントロール（タップ時のみ表示） */}
          {showPlayerControls && (
            <div className="watch-together-page__volume-control--mobile">
              <div className="volume-controls-group">
                <button
                  className="volume-mute-button"
                  onClick={handleMuteToggle}
                  title={isMuted ? "ミュート解除" : "ミュート"}
                >
                  <VolumeIcon size={20} volume={volume} isMuted={isMuted} />
                </button>
                <div className="volume-slider-container">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={isMuted ? 0 : volume}
                    onChange={(e) =>
                      handleVolumeChange(parseInt(e.target.value))
                    }
                    className="volume-slider"
                    disabled={!isPlayerReadyRef.current}
                  />
                </div>
                <span className="volume-percentage">
                  {isMuted ? 0 : volume}%
                </span>
                <button
                  type="button"
                  onClick={handleDanmakuToggle}
                  className="danmaku-toggle-button"
                  title={
                    showDanmaku ? "コメント弾幕を非表示" : "コメント弾幕を表示"
                  }
                >
                  {showDanmaku ? (
                    <CommentIcon size={20} />
                  ) : (
                    <CommentOffIcon size={20} />
                  )}
                </button>
                {/* 全画面ボタン（モバイルのみ） */}
                {window.innerWidth <= 1024 && (
                  <button
                    type="button"
                    onClick={handleFullScreenToggle}
                    className="fullscreen-toggle-button"
                    title={
                      isFullScreenMode
                        ? "全画面モード終了"
                        : "全画面モード（横画面固定）"
                    }
                  >
                    {isFullScreenMode ? (
                      <ExitFullScreenIcon size={20} />
                    ) : (
                      <FullScreenIcon size={20} />
                    )}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* コメント弾幕（プレイヤー内に配置） */}
          {showDanmaku && (
            <CommentDanmaku
              comments={comments.slice(-10)}
              containerRef={playerContainerRef}
            />
          )}

          {/* チャット表示切り替えオーバーレイボタン（全画面モード時のみ） */}
          {isFullScreenMode && (
            <button
              className="chat-toggle-overlay"
              onClick={handleChatToggle}
              title={isChatVisible ? "チャットを非表示" : "チャットを表示"}
              type="button"
            >
              <ChatToggleIcon size={24} isVisible={isChatVisible} />
            </button>
          )}
        </div>
      </section>

      {/* コンテンツエリア（プレイヤー下のセクション用）- 全画面時は非表示 */}
      {!isFullScreenMode && (
        <div className="watch-together-page__content-area">
          {/* 動画情報セクション */}
          <VideoInfoSection
            currentVideo={currentVideo}
            viewerCount={viewerCount}
            connectionStatus={connectionStatus}
            playerState={playerState}
          />

          {/* コンパクトチャット */}
          <CompactChat
            comments={comments}
            onExpand={handleChatExpand}
            commentCount={comments.length}
          />

          {/* 展開チャットオーバーレイ */}
          <ExpandedChatOverlay
            isOpen={isChatExpanded}
            onClose={handleChatClose}
            comments={comments}
            commentText={commentText}
            onCommentTextChange={handleCommentTextChange}
            onCommentSubmit={handleCommentSubmit}
            username={username}
            onUsernameChange={handleUsernameChange}
            userId={userId}
            shouldAutoScroll={shouldAutoScroll}
            showScrollToBottom={showScrollToBottom}
            onScrollToBottom={scrollToBottom}
            onChatScroll={handleChatScroll}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          />
        </div>
      )}

      {/* モバイル全画面横向き時のデスクトップチャットセクション */}
      {isFullScreenMode && (
        <section className="watch-together-page__chat-section">
          <div className="watch-together-page__chat-container">
            <div className="watch-together-page__chat-header">
              <h3>コメント</h3>
              <div className="chat-header-actions">
                <input
                  type="text"
                  value={username}
                  onChange={(e) => handleUsernameChange(e.target.value)}
                  placeholder="ユーザー名を入力"
                  className="username-input"
                  maxLength={20}
                />
              </div>
            </div>

            {/* チャットメッセージ */}
            <div
              className="watch-together-page__chat-messages"
              ref={chatMessagesRef}
              onScroll={handleChatScroll}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {comments.length === 0 ? (
                <div className="chat-empty">
                  <p>💬 コメントがありません</p>
                  <p>最初のコメントを投稿してみましょう！</p>
                </div>
              ) : (
                comments.slice(-50).map((comment) => (
                  <div key={comment.id} className="chat-message">
                    <span
                      className={`chat-username ${
                        comment.userId === userId
                          ? "chat-username--own"
                          : "chat-username--other"
                      }`}
                    >
                      {comment.username}:
                    </span>
                    <span className="chat-message-content">{comment.text}</span>
                  </div>
                ))
              )}

              {/* 最下部へスクロールボタン */}
              {showScrollToBottom && (
                <button
                  className="scroll-to-bottom-button"
                  onClick={scrollToBottom}
                  type="button"
                  title="最新のコメントへ"
                >
                  最新
                </button>
              )}
            </div>

            {/* チャット入力 */}
            <form
              onSubmit={handleCommentSubmit}
              className="watch-together-page__chat-input"
            >
              <input
                type="text"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="コメントを入力..."
                className="chat-input"
                maxLength={100}
                disabled={!username.trim()}
              />
              <button
                type="submit"
                disabled={!commentText.trim() || !username.trim()}
                className="chat-send-button"
              >
                <SendIcon size={20} />
              </button>
            </form>
          </div>
        </section>
      )}

      {/* スマホ用スキップ投票モーダル（フラグ有効時） */}
      {VOTING_ENABLED && (
        <SkipVoteModal
          isOpen={isSkipVoteModalOpen}
          skipVoteStatus={skipVoteStatus}
          skipVoteCountdown={skipVoteCountdown}
          userVoted={userVoted}
          skipVoteActive={skipVoteActive}
          onSkipVote={handleSkipVote}
          onClose={() => setIsSkipVoteModalOpen(false)}
        />
      )}

      {/* 管理者UI（簡易）: admin=1 クエリ時にログイン導線のみ表示（ログイン後にツール表示） */}
      {shouldShowAdminEntry && !isAdminMode && (
        <div className="watch-together-page__admin-login-wrapper">
          <button
            type="button"
            onClick={() => setIsAdminLoginOpen(true)}
            className="watch-together-page__admin-login-button"
          >
            <span className="watch-together-page__admin-login-status-dot" />
            管理者ログイン
          </button>
        </div>
      )}
      <AdminLoginModal
        isOpen={isAdminLoginOpen}
        onClose={() => setIsAdminLoginOpen(false)}
        onLoggedIn={() => setIsAdminMode(true)}
      />
      {isAdminMode && <AdminToolbar onLogout={() => setIsAdminMode(false)} />}
    </div>
  );

  /// デスクトップレイアウトのレンダリング
  const renderDesktopLayout = () => (
    <div
      className={`watch-together-page ${
        isFullScreenMode ? "watch-together-page--fullscreen" : ""
      } ${isFullScreenMode && !isChatVisible ? "chat-hidden" : ""}`}
    >
      {/* ヘッダー */}
      {!isFullScreenMode && (
        <header className="watch-together-page__header">
          <div className="watch-together-page__header-content">
            <h1 className="watch-together-page__title">みんなで視聴</h1>
          </div>
        </header>
      )}

      {/* メインコンテンツ */}
      <div className="watch-together-page__main-content">
        <section className="watch-together-page__player-section">
          {/* プレイヤー */}
          <div
            className="watch-together-page__player-container"
            ref={playerContainerRef}
            onClick={handlePlayerControls}
          >
            <div
              id="youtube-player"
              className="watch-together-page__player"
            ></div>

            {/* プレイヤー読み込み中の表示 */}
            {(isInitializing ||
              !currentVideoRef.current ||
              !isPlayerReadyRef.current) && (
              <div className="watch-together-page__player-loading">
                <div className="loading-spinner"></div>
                <p>
                  {isInitializing
                    ? "プレイヤーを初期化中..."
                    : "プレイヤーを読み込み中..."}
                </p>
              </div>
            )}

            {/* 初回再生ボタンオーバーレイ（自動再生制限解除用） */}
            {showInitialPlayButton && isPlayerReadyRef.current && (
              <div className="watch-together-page__initial-play-overlay">
                <button
                  className="initial-play-button"
                  onClick={handleInitialPlay}
                  type="button"
                >
                  <div className="play-icon">
                    <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
                      <circle
                        cx="12"
                        cy="12"
                        r="12"
                        fill="rgba(255, 255, 255, 0.9)"
                      />
                      <polygon points="10,8 16,12 10,16" fill="#333" />
                    </svg>
                  </div>
                  <span className="play-text">再生開始</span>
                  <span className="play-subtext">クリックして視聴を開始</span>
                </button>
              </div>
            )}

            {/* PC用スキップ投票UI（フラグ有効時のみ） */}
            {VOTING_ENABLED &&
              (skipVoteActive && skipVoteStatus ? (
                <SkipVotePanel
                  skipVoteStatus={skipVoteStatus}
                  skipVoteCountdown={skipVoteCountdown}
                  userVoted={userVoted}
                  onSkipVote={handleSkipVote}
                  mode="pc"
                />
              ) : (
                <SkipVoteButton
                  skipVoteActive={skipVoteActive}
                  skipVoteCountdown={skipVoteCountdown}
                  isPlayerReady={isPlayerReadyRef.current}
                  onSkipVoteStart={handleSkipVoteStart}
                  mode="pc"
                />
              ))}

            {/* スマホ用スキップ投票UI（タップ時のみ表示・フラグ有効時） */}
            {VOTING_ENABLED && showPlayerControls && (
              <SkipVoteButton
                skipVoteActive={skipVoteActive}
                skipVoteCountdown={skipVoteCountdown}
                isPlayerReady={isPlayerReadyRef.current}
                onSkipVoteStart={handleSkipVoteStart}
                mode="mobile"
              />
            )}

            {/* スマホ用音量コントロール（タップ時のみ表示） */}
            {showPlayerControls && (
              <div className="watch-together-page__volume-control--mobile">
                <div className="volume-controls-group">
                  <button
                    className="volume-mute-button"
                    onClick={handleMuteToggle}
                    title={isMuted ? "ミュート解除" : "ミュート"}
                  >
                    <VolumeIcon size={20} volume={volume} isMuted={isMuted} />
                  </button>
                  <div className="volume-slider-container">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={isMuted ? 0 : volume}
                      onChange={(e) =>
                        handleVolumeChange(parseInt(e.target.value))
                      }
                      className="volume-slider"
                      disabled={!isPlayerReadyRef.current}
                    />
                  </div>
                  <span className="volume-percentage">
                    {isMuted ? 0 : volume}%
                  </span>
                  <button
                    type="button"
                    onClick={handleDanmakuToggle}
                    className="danmaku-toggle-button"
                    title={
                      showDanmaku
                        ? "コメント弾幕を非表示"
                        : "コメント弾幕を表示"
                    }
                  >
                    {showDanmaku ? (
                      <CommentIcon size={20} />
                    ) : (
                      <CommentOffIcon size={20} />
                    )}
                  </button>
                  {/* 全画面ボタン（モバイルのみ） */}
                  {window.innerWidth <= 1024 && (
                    <button
                      type="button"
                      onClick={handleFullScreenToggle}
                      className="fullscreen-toggle-button"
                      title={
                        isFullScreenMode
                          ? "全画面モード終了"
                          : "全画面モード（横画面固定）"
                      }
                    >
                      {isFullScreenMode ? (
                        <ExitFullScreenIcon size={20} />
                      ) : (
                        <FullScreenIcon size={20} />
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* 音量コントロール */}
            <div className="watch-together-page__volume-control">
              <button
                type="button"
                className="volume-mute-button"
                onClick={handleMuteToggle}
                title={isMuted ? "ミュート解除" : "ミュート"}
              >
                <VolumeIcon size={20} volume={volume} isMuted={isMuted} />
              </button>
              <input
                type="range"
                min="0"
                max="100"
                value={isMuted ? 0 : volume}
                onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
                className="volume-slider"
                disabled={!isPlayerReadyRef.current}
              />
              <span className="volume-percentage">{isMuted ? 0 : volume}%</span>
              <button
                type="button"
                onClick={handleDanmakuToggle}
                className="danmaku-toggle-button"
                title={
                  showDanmaku ? "コメント弾幕を非表示" : "コメント弾幕を表示"
                }
              >
                {showDanmaku ? (
                  <CommentIcon size={20} />
                ) : (
                  <CommentOffIcon size={20} />
                )}
              </button>
              {/* 全画面ボタン */}
              <button
                type="button"
                onClick={handleFullScreenToggle}
                className="fullscreen-toggle-button"
                title={
                  isFullScreenMode
                    ? "全画面モード終了"
                    : "全画面モード（横画面固定）"
                }
              >
                {isFullScreenMode ? (
                  <ExitFullScreenIcon size={20} />
                ) : (
                  <FullScreenIcon size={20} />
                )}
              </button>
            </div>

            {/* コメント弾幕（プレイヤー内に配置） */}
            {showDanmaku && (
              <CommentDanmaku
                comments={comments.slice(-10)}
                containerRef={playerContainerRef}
              />
            )}

            {/* チャット表示切り替えオーバーレイボタン（全画面モード時のみ） */}
            {isFullScreenMode && (
              <button
                className="chat-toggle-overlay"
                onClick={handleChatToggle}
                title={isChatVisible ? "チャットを非表示" : "チャットを表示"}
                type="button"
              >
                <ChatToggleIcon size={24} isVisible={isChatVisible} />
              </button>
            )}
          </div>
        </section>

        {/* 動画情報セクション */}
        {!isFullScreenMode && (
          <VideoInfoSection
            currentVideo={currentVideoRef.current}
            viewerCount={viewerCount}
            connectionStatus={connectionStatus}
            playerState={playerStateRef.current}
          />
        )}

        {/* チャットセクション */}
        <section className="watch-together-page__chat-section">
          <div className="watch-together-page__chat-container">
            <div className="watch-together-page__chat-header">
              <h3>コメント</h3>
              <div className="chat-header-actions">
                <input
                  type="text"
                  value={username}
                  onChange={(e) => handleUsernameChange(e.target.value)}
                  placeholder="ユーザー名を入力"
                  className="username-input"
                  maxLength={20}
                />
              </div>
            </div>

            {/* チャットメッセージ */}
            <div
              className="watch-together-page__chat-messages"
              ref={chatMessagesRef}
              onScroll={handleChatScroll}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {comments.length === 0 ? (
                <div className="chat-empty">
                  <p>💬 コメントがありません</p>
                  <p>最初のコメントを投稿してみましょう！</p>
                </div>
              ) : (
                comments.slice(-50).map((comment) => (
                  <div key={comment.id} className="chat-message">
                    <span
                      className={`chat-username ${
                        comment.userId === userId
                          ? "chat-username--own"
                          : "chat-username--other"
                      }`}
                    >
                      {comment.username}:
                    </span>
                    <span className="chat-message-content">{comment.text}</span>
                    <span className="chat-timestamp">
                      {formatCommentTimestamp(comment.timestamp)}
                    </span>
                  </div>
                ))
              )}

              {/* 最下部へスクロールボタン */}
              {showScrollToBottom && (
                <button
                  className="scroll-to-bottom-button"
                  onClick={scrollToBottom}
                  type="button"
                  title="最新のコメントへ"
                >
                  最新
                </button>
              )}
            </div>

            {/* チャット入力 */}
            <form
              onSubmit={handleCommentSubmit}
              className="watch-together-page__chat-input"
            >
              <input
                type="text"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="コメントを入力..."
                className="chat-input"
                maxLength={100}
                disabled={!username.trim()}
              />
              <button
                type="submit"
                disabled={!commentText.trim() || !username.trim()}
                className="chat-send-button"
              >
                <SendIcon size={20} />
              </button>
            </form>
          </div>
        </section>
      </div>

      {/* スマホ用スキップ投票モーダル（フラグ有効時） */}
      {VOTING_ENABLED && (
        <SkipVoteModal
          isOpen={isSkipVoteModalOpen}
          skipVoteStatus={skipVoteStatus}
          skipVoteCountdown={skipVoteCountdown}
          userVoted={userVoted}
          skipVoteActive={skipVoteActive}
          onSkipVote={handleSkipVote}
          onClose={() => setIsSkipVoteModalOpen(false)}
        />
      )}

      {/* 管理者UI（簡易）: admin=1 クエリ時にログイン導線のみ表示（ログイン後にツール表示） */}
      {shouldShowAdminEntry && !isAdminMode && (
        <div className="watch-together-page__admin-login-wrapper">
          <button
            type="button"
            onClick={() => setIsAdminLoginOpen(true)}
            className="watch-together-page__admin-login-button"
          >
            <span className="watch-together-page__admin-login-status-dot" />
            管理者ログイン
          </button>
        </div>
      )}
      <AdminLoginModal
        isOpen={isAdminLoginOpen}
        onClose={() => setIsAdminLoginOpen(false)}
        onLoggedIn={() => setIsAdminMode(true)}
      />
      {isAdminMode && <AdminToolbar onLogout={() => setIsAdminMode(false)} />}
    </div>
  );

  /// 条件分岐レンダリング
  return isMobile ? renderMobileLayout() : renderDesktopLayout();
};

export default WatchTogetherPage;
